import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useToast } from '../../hooks/use-toast';
import { 
  Shield, 
  Upload, 
  Download, 
  Trash2, 
  Image, 
  Check, 
  AlertCircle,
  Eye,
  FileText,
  Loader2
} from 'lucide-react';

// Helper function to get authenticated headers
const getAuthHeaders = (additionalHeaders = {}) => {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    ...additionalHeaders
  };
};

const AssetManager = () => {
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [uploadingSeal, setUploadingSeal] = useState(false);
  const [stampProgress, setStampProgress] = useState(0);
  const [sealProgress, setSealProgress] = useState(0);
  
  // Sample lesson note state
  const [sampleNote, setSampleNote] = useState(null);
  const [uploadingSample, setUploadingSample] = useState(false);
  const [sampleProgress, setSampleProgress] = useState(0);
  const [sampleTitle, setSampleTitle] = useState('Sample Lesson Note');
  const [sampleDescription, setSampleDescription] = useState('');
  
  const { toast } = useToast();

  const { handleSubmit } = useForm();

  // Load existing assets
  const loadAssets = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/commissioner-assets`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const assetData = await response.json();
        setAssets(assetData);
      } else if (response.status === 404) {
        // No assets uploaded yet
        setAssets(null);
      } else {
        throw new Error('Failed to load assets');
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      toast({
        title: "Error",
        description: "Failed to load commissioner assets",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Load sample lesson note
  const loadSampleNote = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/sample-lesson-note`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.exists) {
          setSampleNote(data);
        } else {
          setSampleNote(null);
        }
      }
    } catch (error) {
      console.error('Error loading sample lesson note:', error);
    }
  };

  // Upload file with progress
  const uploadFile = async (file, type) => {
    const setUploading = type === 'stamp' ? setUploadingStamp : setUploadingSeal;
    const setProgress = type === 'stamp' ? setStampProgress : setSealProgress;
    
    setUploading(true);
    setProgress(0);
    
    // Check if auth token exists
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast({
        title: "Authentication Required",
        description: "Please log in again to upload files",
        variant: "destructive"
      });
      setUploading(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      console.log(`📤 Uploading ${type}:`, { fileName: file.name, fileSize: file.size, fileType: file.type });
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Convert file to base64
      const fileUrl = await convertFileToBase64(file);
      console.log(`📄 ${type} converted to base64, length:`, fileUrl.length);
      
      const updateData = {
        [type === 'stamp' ? 'stamp_url' : 'seal_url']: fileUrl
      };

      console.log(`📡 Sending ${type} to:`, `${backendUrl}/api/admin/commissioner-assets`);
      const response = await fetch(`${backendUrl}/api/admin/commissioner-assets`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      clearInterval(progressInterval);
      setProgress(100);

      console.log(`📥 ${type} upload response:`, response.status, response.statusText);

      if (response.ok) {
        const updatedAssets = await response.json();
        console.log(`✅ ${type} uploaded successfully:`, updatedAssets);
        setAssets(updatedAssets);
        
        toast({
          title: "Success",
          description: `${type === 'stamp' ? 'Stamp' : 'Seal'} uploaded successfully`,
          variant: "default"
        });
      } else {
        // Get detailed error message from response
        let errorMessage = `Failed to upload ${type}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        console.error(`❌ Upload ${type} failed:`, errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      toast({
        title: "Error",
        description: error.message || `Failed to upload ${type === 'stamp' ? 'stamp' : 'seal'}`,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Upload sample lesson note PDF
  const uploadSampleNote = async (file) => {
    setUploadingSample(true);
    setSampleProgress(0);

    // Check if auth token exists
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast({
        title: "Authentication Required",
        description: "Please log in again to upload files",
        variant: "destructive"
      });
      setUploadingSample(false);
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', sampleTitle);
      formData.append('description', sampleDescription);
      
      console.log('📤 Uploading sample lesson note:', { title: sampleTitle, fileName: file.name, fileSize: file.size });
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setSampleProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await fetch(`${backendUrl}/api/admin/sample-lesson-note`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      clearInterval(progressInterval);
      setSampleProgress(100);

      console.log('📥 Sample lesson note upload response:', response.status, response.statusText);

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Sample lesson note uploaded successfully:', result);
        await loadSampleNote();
        
        toast({
          title: "Success",
          description: "Sample lesson note uploaded successfully",
          variant: "default"
        });
      } else {
        let errorMessage = 'Failed to upload sample lesson note';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        console.error('❌ Upload failed:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error uploading sample lesson note:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload sample lesson note",
        variant: "destructive"
      });
    } finally {
      setUploadingSample(false);
      setSampleProgress(0);
    }
  };

  // Delete sample lesson note
  const deleteSampleNote = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/admin/sample-lesson-note`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setSampleNote(null);
        toast({
          title: "Success",
          description: "Sample lesson note deleted successfully",
          variant: "default"
        });
      } else {
        throw new Error('Failed to delete sample lesson note');
      }
    } catch (error) {
      console.error('Error deleting sample lesson note:', error);
      toast({
        title: "Error",
        description: "Failed to delete sample lesson note",
        variant: "destructive"
      });
    }
  };

  // Sample note dropzone
  const onSampleNoteDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid File",
          description: "Please upload a PDF file",
          variant: "destructive"
        });
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please upload a PDF smaller than 10MB",
          variant: "destructive"
        });
        return;
      }

      uploadSampleNote(file);
    }
  }, [sampleTitle, sampleDescription]);

  const { getRootProps: getSampleRootProps, getInputProps: getSampleInputProps, isDragActive: isSampleDragActive } = useDropzone({
    onDrop: onSampleNoteDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    disabled: uploadingSample
  });

  // Convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Stamp dropzone
  const onStampDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File",
          description: "Please upload an image file (PNG, JPG, etc.)",
          variant: "destructive"
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please upload an image smaller than 5MB",
          variant: "destructive"
        });
        return;
      }

      uploadFile(file, 'stamp');
    }
  }, []);

  // Seal dropzone
  const onSealDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File",
          description: "Please upload an image file (PNG, JPG, etc.)",
          variant: "destructive"
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please upload an image smaller than 5MB",
          variant: "destructive"
        });
        return;
      }

      uploadFile(file, 'seal');
    }
  }, []);

  const {
    getRootProps: getStampRootProps,
    getInputProps: getStampInputProps,
    isDragActive: isStampDragActive
  } = useDropzone({
    onDrop: onStampDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    multiple: false,
    disabled: uploadingStamp
  });

  const {
    getRootProps: getSealRootProps,
    getInputProps: getSealInputProps,
    isDragActive: isSealDragActive
  } = useDropzone({
    onDrop: onSealDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    multiple: false,
    disabled: uploadingSeal
  });

  // Delete asset
  const deleteAsset = async (type) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const updateData = {
        [type === 'stamp' ? 'stamp_url' : 'seal_url']: null
      };

      const response = await fetch(`${backendUrl}/api/admin/commissioner-assets`, {
        method: 'POST',
        headers: getAuthHeaders({
          'Content-Type': 'application/json',
        }),
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedAssets = await response.json();
        setAssets(updatedAssets);
        
        toast({
          title: "Success",
          description: `${type === 'stamp' ? 'Stamp' : 'Seal'} deleted successfully`,
          variant: "default"
        });
      } else {
        throw new Error(`Failed to delete ${type}`);
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      toast({
        title: "Error",
        description: `Failed to delete ${type === 'stamp' ? 'stamp' : 'seal'}`,
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    loadAssets();
    loadSampleNote();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Commissioner Assets - LIVE VERSION ⚡
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading assets...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Commissioner Assets - LIVE VERSION ⚡
          </CardTitle>
          <CardDescription>
            Upload and manage commissioner stamps and seals for legal document embedding
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Document Embedding</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Uploaded stamps and seals will be automatically embedded on Affidavits, Power of Attorney, and Statutory Declaration documents below "Sworn before me this day..."
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Commissioner Stamp */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Commissioner Stamp</CardTitle>
                <CardDescription>
                  Upload the official commissioner stamp image
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {/* Current Stamp */}
                {assets?.stamp_url ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="h-4 w-4" />
                      <span className="text-sm font-medium">Stamp Uploaded</span>
                    </div>
                    
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <img 
                        src={assets.stamp_url} 
                        alt="Commissioner Stamp" 
                        className="max-w-full max-h-32 mx-auto object-contain"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(assets.stamp_url, '_blank')}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteAsset('stamp')}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-gray-500">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">No stamp uploaded</span>
                    </div>
                  </div>
                )}

                {/* Upload Area */}
                <div
                  {...getStampRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isStampDragActive 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  } ${uploadingStamp ? 'pointer-events-none opacity-50' : ''}`}
                >
                  <input {...getStampInputProps()} />
                  
                  {uploadingStamp ? (
                    <div className="space-y-2">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                      <p className="text-sm text-gray-600">Uploading stamp...</p>
                      <Progress value={stampProgress} className="w-full" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-8 w-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {isStampDragActive
                          ? 'Drop the stamp image here...'
                          : 'Drag & drop stamp image here, or click to select'}
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>

            {/* Commissioner Seal */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Commissioner Seal</CardTitle>
                <CardDescription>
                  Upload the official commissioner seal image
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                
                {/* Current Seal */}
                {assets?.seal_url ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-600">
                      <Check className="h-4 w-4" />
                      <span className="text-sm font-medium">Seal Uploaded</span>
                    </div>
                    
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <img 
                        src={assets.seal_url} 
                        alt="Commissioner Seal" 
                        className="max-w-full max-h-32 mx-auto object-contain"
                      />
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(assets.seal_url, '_blank')}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteAsset('seal')}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-gray-500">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">No seal uploaded</span>
                    </div>
                  </div>
                )}

                {/* Upload Area */}
                <div
                  {...getSealRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isSealDragActive 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  } ${uploadingSeal ? 'pointer-events-none opacity-50' : ''}`}
                >
                  <input {...getSealInputProps()} />
                  
                  {uploadingSeal ? (
                    <div className="space-y-2">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                      <p className="text-sm text-gray-600">Uploading seal...</p>
                      <Progress value={sealProgress} className="w-full" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-8 w-8 mx-auto text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {isSealDragActive
                          ? 'Drop the seal image here...'
                          : 'Drag & drop seal image here, or click to select'}
                      </p>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF up to 5MB
                      </p>
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>
          </div>

          {/* Usage Information */}
          <Card className="bg-gray-50 border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg">Embedding Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 rounded-full p-1">
                    <Image className="h-3 w-3 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Automatic Document Embedding</p>
                    <p className="text-gray-600">
                      When both stamp and seal are uploaded, they will automatically be embedded on:
                    </p>
                    <ul className="list-disc list-inside mt-1 text-gray-600 space-y-1">
                      <li>Affidavit documents</li>
                      <li>Power of Attorney documents</li>
                      <li>Statutory Declaration documents</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 rounded-full p-1">
                    <FileText className="h-3 w-3 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Placement Location</p>
                    <p className="text-gray-600">
                      Assets will be placed inline at the bottom of documents, positioned below the text "Sworn before me this day..."
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </CardContent>
      </Card>

      {/* Sample Lesson Note Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Sample Lesson Note for Homepage
          </CardTitle>
          <CardDescription>
            Upload a sample lesson note PDF to display on the homepage hero section. 
            Visitors can view and read the document but cannot download it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Info Banner */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Eye className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-purple-900">View-Only Display</h3>
                <p className="text-sm text-purple-700 mt-1">
                  The uploaded PDF will be displayed in an embedded viewer on the homepage. 
                  Users can expand and read the document but downloading is disabled.
                </p>
              </div>
            </div>
          </div>

          {/* Current Sample Note */}
          {sampleNote ? (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-green-600">
                  <Check className="h-4 w-4" />
                  <span className="text-sm font-medium">Sample Lesson Note Uploaded</span>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={deleteSampleNote}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium">{sampleNote.title}</p>
                    <p className="text-sm text-gray-500">{sampleNote.filename}</p>
                  </div>
                </div>
                {sampleNote.description && (
                  <p className="text-sm text-gray-600">{sampleNote.description}</p>
                )}
                <p className="text-xs text-gray-400">
                  Uploaded: {new Date(sampleNote.uploaded_at).toLocaleString()}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Title and Description inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={sampleTitle}
                    onChange={(e) => setSampleTitle(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="Sample Lesson Note"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description (optional)</label>
                  <input
                    type="text"
                    value={sampleDescription}
                    onChange={(e) => setSampleDescription(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm"
                    placeholder="A sample KG 1 English lesson note"
                  />
                </div>
              </div>
              
              {/* Upload Dropzone */}
              <div
                {...getSampleRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${isSampleDragActive ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'}`}
              >
                <input {...getSampleInputProps()} />
                <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">
                  {isSampleDragActive ? 'Drop the PDF here...' : 'Drag & drop a PDF file here, or click to select'}
                </p>
                <p className="text-xs text-gray-400 mt-1">PDF only, max 10MB</p>
              </div>

              {/* Upload Progress */}
              {uploadingSample && (
                <div className="space-y-2">
                  <Progress value={sampleProgress} className="h-2" />
                  <p className="text-sm text-center text-gray-500">Uploading... {sampleProgress}%</p>
                </div>
              )}
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  );
};

export default AssetManager;